import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { usePermissions } from '../hooks/usePermissions';
import { collection, query, where, getDocs, orderBy, limit, getCountFromServer, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import OrderDetails from '../components/orders/OrderDetails';
import DemoDetailsModal from '../components/demos/DemoDetailsModal';
import AgentFileUploadModal from '../components/files/AgentFileUploadModal';
import { 
  CalendarIcon, 
  ShoppingBagIcon, 
  ArrowRightIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  PhoneIcon,
  PlusIcon,
  XMarkIcon,
  ChatBubbleBottomCenterTextIcon,
  CloudArrowUpIcon,
  GiftIcon
} from '@heroicons/react/24/outline';
import { shareOnWhatsApp } from '../utils/shareUtils';

// Helper function to normalize different date formats to a Date object
const normalizeDate = (date) => {
  if (!date) return null;
  try {
    // If it's a Firestore Timestamp
    if (typeof date === 'object' && date.toDate) {
      return date.toDate();
    }
    // If it's an ISO date string
    if (typeof date === 'string') {
      // Handle both full ISO strings and date-only strings
      const parsedDate = date.includes('T') 
        ? new Date(date) 
        : new Date(date + 'T00:00:00.000Z');
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    // If it's already a Date object
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  } catch (error) {
    console.error('Error normalizing date:', error, 'Input:', date);
    return null;
  }
};

export default function Dashboard() {
  const { currentAgent } = useAuth();
  const { getPendingDemos } = useDemo();
  const { isHost, isBooking } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingDemos: 0,
    totalResources: 0
  });

  const [pendingDemos, setPendingDemos] = useState([]);
  const [agentResources, setAgentResources] = useState([]);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Host gifts state
  const [hostGifts, setHostGifts] = useState([]);
  const [hostGiftsLoading, setHostGiftsLoading] = useState(true);
  const [selectedHostGift, setSelectedHostGift] = useState(null);
  const [showHostGiftModal, setShowHostGiftModal] = useState(false);
  
  // Pagination states
  const [currentDemoPage, setCurrentDemoPage] = useState(1);
  const [currentOrderPage, setCurrentOrderPage] = useState(1);
  const itemsPerPage = 5;

  // Function to count all active, unexpired resources
  const countAgentResources = async () => {
    if (!currentAgent?.id) return 0;
    
    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of day for date comparison
      
      const resourcesRef = collection(db, 'x_agents_resources');
      
      // Get all active resources
      const q = query(
        resourcesRef,
        where('active', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      let count = 0;
      
      // Filter in memory to handle both Timestamp and ISO date strings
      querySnapshot.forEach((doc) => {
        try {
          const data = doc.data();
          const expiryDate = normalizeDate(data.expiry_date);
          
          // Only count if expiry date is valid and today or in the future
          if (expiryDate) {
            const expiryDateAtStartOfDay = new Date(expiryDate);
            expiryDateAtStartOfDay.setHours(0, 0, 0, 0);
            
            if (expiryDateAtStartOfDay >= now) {
              count++;
            }
          }
        } catch (error) {
          console.error('Error processing document:', doc.id, error);
        }
      });
      
      // Update the stats with the total count
      setStats(prev => ({
        ...prev,
        totalResources: count
      }));
      
      return count;
    } catch (error) {
      console.error('Error counting agent resources:', error);
      return 0;
    }
  };
  
  // Function to load agent resources for the dashboard
  const loadAgentResources = async () => {
    if (!currentAgent?.id) {
      console.log('No current agent ID, skipping agent resources load');
      return;
    }
    
    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of day for date comparison
      
      const resourcesRef = collection(db, 'x_agents_resources');
      
      // First, count all active, unexpired resources
      await countAgentResources();
      
      // Get all active dashboard resources
      const q = query(
        resourcesRef,
        where('active', '==', true),
        where('dashboard_view', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const resources = [];
      
      querySnapshot.forEach((doc) => {
        try {
          const data = doc.data();
          const expiryDate = normalizeDate(data.expiry_date);
          
          // Skip if expiry date is invalid or in the past
          if (!expiryDate) return;
          
          const expiryDateAtStartOfDay = new Date(expiryDate);
          expiryDateAtStartOfDay.setHours(0, 0, 0, 0);
          
          if (expiryDateAtStartOfDay < now) return;
          
          resources.push({
            id: doc.id,
            title: data.title || 'Untitled Document',
            url: data.url || '#',
            expiry_date: expiryDate, // Store as normalized Date object
            active: data.active !== false, // Default to true if not specified
            dashboard_view: data.dashboard_view === true, // Ensure boolean
            mimeType: data.mimeType || 'application/octet-stream',
            description: data.description || '',
            sendable: data.sendable || false,
            sending_text: data.sending_text || ''
          });
        } catch (error) {
          console.error('Error processing document:', doc.id, error);
        }
      });
      
      // Sort alphabetically by title (case-insensitive)
      resources.sort((a, b) => {
        const titleA = a.title?.toLowerCase() || '';
        const titleB = b.title?.toLowerCase() || '';
        return titleA.localeCompare(titleB);
      });
      
      // Take only the first 12 documents after sorting
      setAgentResources(resources.slice(0, 12));
      
    } catch (error) {
      console.error('Error in loadAgentResources:', error);
      // Try a simpler query if the main one fails
      try {
        const fallbackQuery = query(
          collection(db, 'x_agents_resources'),
          where('active', '==', true),
          limit(12)
        );
        
        const fallbackSnapshot = await getDocs(fallbackQuery);
        const fallbackResources = [];
        
        fallbackSnapshot.forEach((doc) => {
          const data = doc.data();
          fallbackResources.push({
            id: doc.id,
            title: data.title,
            url: data.url,
            expiry_date: data.expiry_date,
            active: data.active,
            dashboard_view: data.dashboard_view
          });
        });
        
        setAgentResources(fallbackResources);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
      }
    }
  };

  // Function to load dashboard data
  const loadDashboardData = async () => {
    if (!currentAgent?.id) return;
    
    try {
        setLoading(true);
        
        // Use currentAgent.id and agent_id directly like OrdersList.jsx and DemoContext.jsx
        const agentId = currentAgent.agent_id || currentAgent.id;
        
        // Load orders based on role
        let ordersQuery;
        if (isHost) {
          // Hosts see only their own orders
          ordersQuery = query(
            collection(db, 'orders'),
            where('agent_id', '==', currentAgent.id),
            orderBy('created_at', 'desc'),
            limit(4)
          );
        } else {
          // Admin, team leaders, and users see all orders
          ordersQuery = query(
            collection(db, 'orders'),
            orderBy('created_at', 'desc'),
            limit(4)
          );
        }
        
        // Execute the orders query with error handling
        let ordersSnapshot;
        try {
          ordersSnapshot = await getDocs(ordersQuery);
        } catch (error) {
          console.error('Error executing orders query:', error);
          throw error; // Re-throw to be caught by the outer try-catch
        }
        
        
        // Set up date range for demos (using agent_id like DemoContext.jsx)
        const demosRef = collection(db, 'demo_bookings');
        
        // Set date range for upcoming demos (from today onwards)
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        // Load demos based on role
        let demosQuery;
        if (isHost) {
          // Hosts see ONLY demos where they are the agent OR hosting
          demosQuery = query(
            demosRef,
            where('payload.agentCode', '==', currentAgent.agent_id),
            where('payload.dateTime.demoDate', '>=', todayStr),
            orderBy('payload.dateTime.demoDate', 'asc'),
            limit(4)
          );
        } else {
          // Regular agents and admins see their own demos
          demosQuery = query(
            demosRef,
            where('payload.agentCode', '==', agentId),
            where('payload.dateTime.demoDate', '>=', todayStr),
            orderBy('payload.dateTime.demoDate', 'asc'),
            limit(4)
          );
        }

        const demosSnapshot = await getDocs(demosQuery);
        
        // Process and sort orders by created_at
        const ordersData = [];
        ordersSnapshot.forEach((doc) => {
          ordersData.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort by created_at in descending order (already limited to 4 by the query)
        ordersData.sort((a, b) => {
          const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(0);
          const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(0);
          return dateB - dateA;
        });
        
        // Process demos (query already filters by agentCode, no need for additional filtering)
        const demosData = [];
        
        demosSnapshot.forEach((doc) => {
          const data = doc.data();
          const demoDate = data.payload?.dateTime?.demoDate;
          
          demosData.push({
            id: doc.id,
            ...data, // Include all document data
            payload: {
              ...data.payload,
              dateTime: {
                demoDate: demoDate,
                timeSlot: data.payload?.dateTime?.timeSlot || 'No time set',
                ...data.payload?.dateTime
              },
              customer: {
                name: data.payload?.customer?.name || 'New Customer',
                ...data.payload?.customer
              },
              hostedDemo: {
                isHosted: data.payload?.hostedDemo?.isHosted || 'NO',
                ...data.payload?.hostedDemo
              },
              location: data.payload?.location || 'Location not specified'
            }
          });
        });
        
        // Sort demos by date (closest first)
        const sortedDemos = demosData.sort((a, b) => {
          const dateA = a.payload?.dateTime?.demoDate ? new Date(a.payload.dateTime.demoDate) : new Date(0);
          const dateB = b.payload?.dateTime?.demoDate ? new Date(b.payload.dateTime.demoDate) : new Date(0);
          return dateA - dateB; // Sort ascending (closest first)
        });
        
        setRecentOrders(ordersData);
        setPendingDemos(sortedDemos);
        
        // Get total counts for role-specific data
        let totalOrdersQuery, totalDemosQuery;
        const ordersRef = collection(db, 'orders'); // Define ordersRef here
        
        if (isHost) {
          totalOrdersQuery = query(ordersRef, where('agent_id', '==', currentAgent.agent_id));
          totalDemosQuery = query(
            demosRef,
            where('payload.agentCode', '==', currentAgent.agent_id),
            where('payload.dateTime.demoDate', '>=', today.toISOString().split('T')[0])
          );
        } else {
          totalOrdersQuery = query(ordersRef, where('agent_id', '==', currentAgent.id));
          totalDemosQuery = query(
            demosRef,
            where('payload.agentCode', '==', agentId),
            where('payload.dateTime.demoDate', '>=', today.toISOString().split('T')[0])
          );
        }
        
        const [totalOrdersSnapshot, totalDemosSnapshot] = await Promise.all([
          getCountFromServer(totalOrdersQuery),
          getCountFromServer(totalDemosQuery)
        ]);
        
        // Update stats with total counts
        setStats({
          totalOrders: totalOrdersSnapshot.data().count,
          pendingDemos: totalDemosSnapshot.data().count
        });
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
  // Load data on component mount and when agent changes
  useEffect(() => {
    // Create an async function to handle the data loading
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadDashboardData(),
          loadAgentResources()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial load
    loadData();
    
    // Auto-refresh disabled to prevent interrupting user interactions
    // Users can manually refresh if needed
  }, [currentAgent?.id]); // Only depend on currentAgent.id

  useEffect(() => {
    const controller = new AbortController();

    const syncAppVersion = async () => {
      try {
        const response = await fetch('/app-version.json', {
          cache: 'no-store',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch version manifest: ${response.status}`);
        }

        const data = await response.json();
        if (data?.version) {
          localStorage.setItem('latestAppVersion', data.version);
        }
        if (data?.buildId) {
          localStorage.setItem('appBuildId', data.buildId);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Version manifest sync warning:', error);
        }
      }
    };

    syncAppVersion();

    return () => controller.abort();
  }, []);
  
  // Fetch host gifts for host and booking agents
  useEffect(() => {
    if (!isHost && !isBooking) return;
    
    const fetchHostGifts = async () => {
      try {
        setHostGiftsLoading(true);
        const productsRef = collection(db, 'products');
        
        // Query for products that contain "HstGft" in their Categories field
        const q = query(
          productsRef,
          where('Categories', '!=', null),
          where('isActive', '==', true)
        );
        
        const querySnapshot = await getDocs(q);
        const productList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Check if Categories field contains "HstGft"
          if (data.Categories && data.Categories.includes('HstGft')) {
            productList.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        setHostGifts(productList);
      } catch (error) {
        console.error('Error fetching host gifts:', error);
      } finally {
        setHostGiftsLoading(false);
      }
    };

    fetchHostGifts();
  }, [isHost, isBooking]);
  
  // Debug effect to log when agentResources changes
  // useEffect(() => {
  //   if (agentResources.length > 0) {
  //     console.log('Agent resources loaded:', agentResources);
  //   }
  // }, [agentResources]);

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };
  
  const handleViewAll = (type) => {
    if (type === 'orders') {
      navigate('/my-orders');
    } else if (type === 'demos') {
      navigate('/my-demos');
    } else if (type === 'documents') {
      navigate('/documents');
    }
  };
  
  const handleDemoClick = (demo) => {
    // Use the same modal approach as in /my-demos
    navigate(`/demos/${demo.id}`, { 
      state: { background: location } 
    });
  };
  
  const handleHostGiftClick = (gift) => {
    setSelectedHostGift(gift);
    setShowHostGiftModal(true);
  };
  
  const closeHostGiftModal = () => {
    setShowHostGiftModal(false);
    setSelectedHostGift(null);
  };
  
  const closeDemoModal = () => {
    setShowDemoModal(false);
    setSelectedDemo(null);
  };
  
  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const handleUploadSuccess = (uploadResult) => {
    console.log('File uploaded successfully:', uploadResult);
    // Show success notification
    alert(`✅ File "${uploadResult.fileName}" uploaded successfully!\n\nFile size: ${uploadResult.fileSizeMB}MB\nGoogle Drive sync in progress...`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Minimal header space */}
      <div className="h-12 flex-shrink-0"></div>
      
      {/* Main content */}
      <div className="flex-1 overflow-y-auto pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Welcome back, {currentAgent?.name || 'Agent'}
            </h1>
          </div>
          
          {/* Host & Booking Users: Show Host Gifts Only */}
          {(isHost || isBooking) ? (
            <div className="bg-[#F7FBFC] p-4 sm:p-6 lg:p-8 -mx-4 sm:-mx-6 lg:-mx-8">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                  <h1 className="text-3xl sm:text-4xl font-bold text-[#011841] mb-2">Hosting Gifts</h1>
                  <p className="text-[#033EA5] text-lg">Browse our collection of host appreciation gifts</p>
                </div>

                {/* Products Grid */}
                {hostGiftsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#033EA5] mx-auto"></div>
                    <p className="mt-4 text-[#033EA5]">Loading host gifts...</p>
                  </div>
                ) : hostGifts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎁</div>
                    <h3 className="text-xl font-semibold text-[#011841] mb-2">No Host Gifts Available</h3>
                    <p className="text-[#033EA5]">Check back later for new host gift selections.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {hostGifts.map((gift) => (
                      <div
                        key={gift.id}
                        className="bg-white rounded-xl shadow-lg border border-[#19BFB7]/20 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105"
                        onClick={() => handleHostGiftClick(gift)}
                      >
                        {/* Product Image */}
                        <div className="w-full h-48 bg-gray-50 relative overflow-hidden">
                          <img
                            src={gift.imageUrl}
                            alt={gift.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <div class="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                                  <div class="text-center">
                                    <div class="text-4xl mb-2">📦</div>
                                    <div class="text-sm">No Image</div>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        </div>

                        {/* Product Info */}
                        <div className="p-4">
                          <h3 className="font-bold text-[#011841] text-lg mb-2 line-clamp-2">{gift.name}</h3>
                          

                          {gift.USP && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{gift.USP}</p>
                          )}

                          <button className="w-full bg-[#19BFB7] hover:bg-[#28E3DA] text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300">
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Regular Users: Show Default Dashboard
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {/* Total Orders */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg mr-4">
                  <ShoppingBagIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Orders</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalOrders || 0}</p>
                </div>
              </div>
            </div>
            
            {/* Upcoming Demos */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg mr-4">
                  <CalendarIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="flex items-baseline space-x-2">
                    <p className="text-sm font-medium text-gray-500">Upcoming Demos</p>
                    <p className="text-lg font-semibold text-gray-900">{pendingDemos.length || 0}</p>
                  </div>
                  <button
                    onClick={() => navigate('/my-feedback')}
                    className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-500 underline"
                  >
                    View Feedback
                  </button>
                </div>
              </div>
            </div>
            
            {/* Document Repository */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Agent Resources</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <button 
                      onClick={() => setShowUploadModal(true)}
                      className="inline-flex items-center text-base font-semibold text-blue-600 hover:text-blue-700"
                    >
                      <CloudArrowUpIcon className="h-5 w-5 mr-1" />
                      Upload Files
                    </button>
                    <span className="text-gray-400">|</span>
                    <button 
                      onClick={() => navigate('/my-uploads')}
                      className="text-base font-semibold text-blue-600 hover:text-blue-700"
                    >
                      My Files
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
          </div>

          {/* Three Column Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Column 1: Recent Orders */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Recent Orders</h2>
                <button 
                  onClick={() => navigate('/my-orders')}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  View All
                </button>
              </div>
              
              {recentOrders.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {recentOrders.map((order) => (
                    <div 
                      key={order.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOrderClick(order)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              Order #{order.orderNumber || 'N/A'}
                            </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              order.status === 'completed' 
                                ? 'bg-green-100 text-green-800' 
                                : order.status === 'processing' 
                                  ? 'bg-yellow-100 text-yellow-800' 
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                              {order.status || 'pending'}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <span className="font-medium">
                              {order.currency || 'BHD'} 
                              {(() => {
                                // Calculate total from items if available
                                if (Array.isArray(order.items) && order.items.length > 0) {
                                  const total = order.items.reduce((sum, item) => {
                                    const price = parseFloat(item.unit_cost_local_currency || 0);
                                    const quantity = parseInt(item.quantity || 1, 10);
                                    return sum + (price * quantity);
                                  }, 0);
                                  return total.toFixed(2);
                                }
                                // Fallback to order.subtotal_local if items not available
                                return order.subtotal_local ? parseFloat(order.subtotal_local).toFixed(2) : '0.00';
                              })()}
                            </span>
                            <span className="mx-1">•</span>
                            <span>
                              {Array.isArray(order.items) ? order.items.length : 0} 
                              {Array.isArray(order.items) && order.items.length === 1 ? 'item' : 'items'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500">No recent orders</p>
                  <button
                    onClick={() => navigate('/my-orders')}
                    className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ShoppingBagIcon className="-ml-0.5 mr-1 h-3 w-3" />
                    View All Orders
                  </button>
                </div>
              )}
            </div>

            {/* Column 2: Upcoming Demos */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Upcoming Demos</h2>
                <button 
                  onClick={() => navigate('/my-demos')}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  View All
                </button>
              </div>
              
              {pendingDemos.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {pendingDemos.map((demo, index) => (
                    <div 
                      key={index}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleDemoClick(demo)}
                    >
                      <div className="flex items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {demo.payload?.customer?.name || 'New Demo'}
                            </p>
                            {demo.payload?.hostedDemo?.isHosted === 'YES' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                Hosted
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>{demo.payload?.dateTime?.demoDate || 'No date set'}</span>
                            <span className="mx-1">•</span>
                            <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>{demo.payload?.dateTime?.timeSlot || 'No time set'}</span>
                          </div>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <MapPinIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="truncate">{demo.payload?.location || 'Location not specified'}</span>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500">No upcoming demos</p>
                  <button
                    onClick={() => navigate('/book-demo')}
                    className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CalendarIcon className="-ml-0.5 mr-1 h-3 w-3" />
                    Schedule a Demo
                  </button>
                </div>
              )}
            </div>

            {/* Column 3: Agent Resources - HIDDEN for hosts and booking agents */}
            {!isHost && !isBooking && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">Quick Docs <span className="text-sm font-normal text-gray-500">({agentResources.length})</span></h2>
                  {/* Previous: onClick={() => handleViewAll('documents')} */}
                  <button 
                    onClick={() => window.open('https://sites.google.com/plsss.vip/plssstraining/agents-resources', '_blank', 'noopener,noreferrer')}
                    className="inline-flex items-center justify-center rounded-full bg-[#022b73] px-5 py-2 text-sm font-semibold text-white shadow hover:bg-[#011c4d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#022b73]"
                  >
                    Training
                  </button>
                </div>
                
                {agentResources.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {agentResources.map((doc) => (
                      <a 
                        key={doc.id}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-4 hover:bg-gray-50 transition-colors duration-150"
                      >
                        <div className="flex items-center justify-between w-full">
                          <p className="text-sm font-medium text-gray-900 break-words pr-2 flex-1">
                            {doc.title}
                          </p>
                          <div className="flex items-center space-x-2">
                            {doc.sendable === true && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  shareOnWhatsApp(doc.url, doc, {
                                    sendable: doc.sendable,
                                    sending_text: doc.sending_text
                                  });
                                }}
                                className="text-green-600 hover:text-green-700 transition-colors"
                                title="Share via WhatsApp"
                              >
                                <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
                              </button>
                            )}
                            <svg className="h-5 w-5 text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-sm text-gray-500">No documents available</p>
                    <button
                      onClick={() => navigate('/documents')}
                      className="mt-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <svg className="-ml-0.5 mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View Documents
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* End Regular Dashboard Content */}
            </>
          )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-medium text-gray-900">Order Details</h2>
              <button 
                onClick={() => setShowOrderModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <OrderDetails order={selectedOrder} onClose={() => setShowOrderModal(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Hosting Gifts Section - Show for Host and Booking Agents */}
      {(isHost || isBooking) && (
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Hosting Gifts Tracking</h2>
              <p className="text-sm text-gray-500">Manage your hosting gift inventory</p>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <GiftIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Hosting Gifts Coming Soon</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Track and manage the hosting gifts you've received. This feature is currently under development.
                </p>
                <div className="space-y-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">What's Coming:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Gift inventory tracking</li>
                      <li>• Gift history analytics</li>
                      <li>• Gift redemption management</li>
                      <li>• Host performance metrics</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Details Modal */}
      {showDemoModal && selectedDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-medium text-gray-900">Demo Details</h2>
              <button 
                onClick={() => setShowDemoModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <DemoDetailsModal 
                demo={selectedDemo} 
                onClose={() => setShowDemoModal(false)} 
                onUpdate={() => {
                  loadDashboardData();
                  setShowDemoModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      <AgentFileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Host Gift Modal */}
      {showHostGiftModal && selectedHostGift && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#F7FBFC] rounded-xl shadow-2xl max-w-4xl max-h-[90vh] overflow-y-auto w-full border border-[#19BFB7]/20">
            <div className="flex justify-between items-center mb-6 p-6 border-b border-[#19BFB7]/20">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#011841]">{selectedHostGift.name}</h2>
              </div>
              <button
                onClick={closeHostGiftModal}
                className="text-[#033EA5] hover:text-[#022b73] text-2xl font-bold p-2 hover:bg-[#E8F4F8] rounded-lg transition-all duration-300"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {/* Product Image */}
              <div className="w-full bg-gray-50 rounded-lg mb-6">
                <div className="w-full h-64 sm:h-96 flex items-center justify-center p-4">
                  <img
                    src={selectedHostGift.imageUrl}
                    alt={selectedHostGift.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.display = 'none';
                      e.target.parentElement.nextElementSibling.style.display = 'flex';
                    }}
                  />
                </div>
                <div className="w-full h-64 sm:h-96 bg-gray-200 flex items-center justify-center text-gray-500" style={{display: 'none'}}>
                  <div className="text-center">
                    <div className="text-6xl mb-4">📦</div>
                    <div className="text-xl">No Image Available</div>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="space-y-6">
                {selectedHostGift.USP && (
                  <div>
                    <h3 className="text-xl font-bold text-[#011841] mb-3">✨ Key Features</h3>
                    <div className="bg-[#F7FBFC] rounded-lg p-4 border border-[#19BFB7]/20">
                      <p className="text-[#033EA5] whitespace-pre-line leading-relaxed">{selectedHostGift.USP}</p>
                    </div>
                  </div>
                )}

                {selectedHostGift.description && (
                  <div>
                    <h3 className="text-xl font-bold text-[#011841] mb-3">📝 Description</h3>
                    <p className="text-gray-600 leading-relaxed">{selectedHostGift.description}</p>
                  </div>
                )}

                {/* Additional Product Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white rounded-lg p-4 border border-[#19BFB7]/20">
                  {selectedHostGift.code && (
                    <div>
                      <span className="font-semibold text-[#011841]">Product Code:</span>
                      <span className="ml-2 text-[#033EA5]">{selectedHostGift.code}</span>
                    </div>
                  )}
                  
                  <div>
                    <span className="font-semibold text-[#011841]">Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-sm ${
                      selectedHostGift.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedHostGift.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex justify-end mt-8">
                <button
                  onClick={closeHostGiftModal}
                  className="px-6 py-3 bg-[#033EA5] hover:bg-[#022b73] text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
